import { getCurrentEnvironment } from "#webapp/src/state/relay-environment";
import { windowLocation } from "#webapp/src/util/location.util";
import { paths } from "@clockwise/client-commons/src/constants/site";
import {
  TrackingEvents,
  getAnonymousId,
  track,
} from "@clockwise/web-commons/src/util/analytics.util";
import { browserName, extensionURLForBrowser } from "@clockwise/web-commons/src/util/browser.util";
import {
  inviteLink,
  signupExperimentExtensionRedirect,
  signupExperimentInitialAnonymousId,
} from "@clockwise/web-commons/src/util/local-storage";
import { Match, Params, RouteProps, RouterState } from "found";
import * as React from "react";
import { GraphQLTaggedNode, QueryRenderer, ReadyState } from "react-relay";

import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import StaticContainer from "react-static-container";

//////////////////
// ROUTE CONFIG
//////////////////

export const noopComponent = StaticContainer;

export function getComponentOrRedirect<T>(
  component: React.ComponentType<T>,
  requirement: (props: RouteProps) => string | undefined,
  props: RouteProps,
  newTab?: boolean,
): React.ComponentType<T> {
  const redirect = requirement(props);
  if (redirect) {
    if (newTab) {
      window.open(redirect, "_blank");
    } else {
      props.router.replace(redirect);
    }
    return noopComponent;
  }

  return component;
}

//////////////////////
// URL SEARCH PARAMS
//////////////////////

export function getSearchParams(resolvedMatch: Match) {
  const search = resolvedMatch.location.search;
  return new URLSearchParams(search);
}

////////////////////////////////////////////////////
// TEMP ROUTE BANDAID FOR DEPRECATING FOUND RELAY //
////////////////////////////////////////////////////

interface RouteQueryOptions {
  prepareVariables?: (params: Params) => object;
}

export const withRouteQuery = <P extends React.PropsWithChildren<RouterState>>(
  ComponentClass: any,
  query: GraphQLTaggedNode | null | undefined,
  { prepareVariables }: RouteQueryOptions = {},
  LoadingComponent?: React.FunctionComponent,
) =>
  function QueryRendererContainer(props: P) {
    const environment = getCurrentEnvironment();

    return (
      <QueryRenderer
        environment={environment}
        query={query}
        variables={prepareVariables ? prepareVariables(props.match.params) : {}}
        render={(readyState: ReadyState) => {
          // check for error or show a loader for first load
          if (!Object.keys(readyState.props ?? {}).length) {
            return !!LoadingComponent ? <LoadingComponent /> : null;
          }

          return <ComponentClass {...props} {...readyState.props} />;
        }}
      />
    );
  };

/**
 * variation of above `withRouteQuery` (described as "TEMP ROUTE BANDAID")
 * that eliminates prepareVariables and Found's RouterState
 * instead returns params and navigate from react-router-dom
 *
 * @deprecated use class components
 * */
export const withRouterQuery = <P extends React.PropsWithChildren>(
  ComponentClass: any,
  query: GraphQLTaggedNode | null | undefined,
  LoadingComponent?: React.FunctionComponent,
) =>
  function QueryRendererContainer(props: P) {
    const navigate = useNavigate();
    const params = useParams();
    const [searchParams] = useSearchParams();
    const environment = getCurrentEnvironment();
    return (
      <QueryRenderer
        environment={environment}
        query={query}
        variables={{}}
        render={(readyState: ReadyState) => {
          // check for error or show a loader for first load
          if (!Object.keys(readyState.props ?? {}).length) {
            return !!LoadingComponent ? <LoadingComponent /> : null;
          }
          return (
            <ComponentClass
              {...props}
              {...readyState.props}
              navigate={navigate}
              params={params}
              searchParams={searchParams}
            />
          );
        }}
      />
    );
  };

////////////////////////////////////////////////////
// Signup or invite redirect //
////////////////////////////////////////////////////

export const requireExtensionStoreRedirect = (
  props: RouteProps,
  Component: React.ComponentType,
  redirect: string,
  isSignupExperiment: boolean,
): React.ComponentType => {
  const isChrome = browserName === "Chrome";
  const isFireFox = browserName === "Firefox";

  if ((isChrome || isFireFox) && !!extensionURLForBrowser) {
    // If firefox or chrome, redirect to relevant extension store
    let windowRedirectString = "InviteExtensionUrlRedirect";

    if (isSignupExperiment) {
      // If signup experiment do some extra tracking
      signupExperimentExtensionRedirect.set(true);
      track(
        TrackingEvents.Q4_2022_SIGNUP_EXPERIMENT.SIGNUP_EXPERIMENT_EXTENSION_REDIRECT,
        undefined,
        undefined,
        true,
      );
      windowRedirectString = "SignupExperimentExtensionUrlRedirect";
      signupExperimentInitialAnonymousId.set(getAnonymousId());
    }

    windowLocation.assign(windowRedirectString, extensionURLForBrowser);
  } else {
    // if no extension store, redirect to supplied redirect path
    if (isSignupExperiment) {
      // If signup experiment do some extra tracking
      track(
        TrackingEvents.Q4_2022_SIGNUP_EXPERIMENT.SIGNUP_EXPERIMENT_SIGNUP_REDIRECT,
        undefined,
        undefined,
        true,
      );
      signupExperimentInitialAnonymousId.set(getAnonymousId());
    }

    props.router.replace(redirect);
  }
  return Component;
};

export const requireInviteAttributionAndRedirect = (
  props: RouteProps,
  Component: React.ComponentType,
  redirect: string,
): React.ComponentType => {
  const referrerToken = props.params?.inviteToken;

  const hasAlreadySignedUpViaInviteLink = !!inviteLink && !!inviteLink.getAll()?.hasSignedUp;

  if (!!referrerToken && !hasAlreadySignedUpViaInviteLink) {
    inviteLink.set("referrer", referrerToken);
    inviteLink.set("hasSignedUp", false);
    // Non debounced tracking event
    track(
      TrackingEvents.INVITE_ATTRIBUTION.INVITE_LINK_ACTIVATED,
      { referrer: referrerToken },
      undefined,
      true,
    );
  }
  const whereToGoNext = redirect ? redirect : paths.welcome;
  props.router.replace(whereToGoNext);
  return Component;
};
